import { useRouter } from 'next/navigation';
import React from 'react';

import { trackGAEvent } from '@/utility/analytics';
import { ANNOUNCEMENT_CARD_EVENTS } from '@/utility/analyticsConsts';
import { openInNewTab } from '@/utility/helpers';
import { t } from '@/utility/localization';

import { useWindowWidthContext } from '@/contexts/WindowWidthContext';

import NextImage from '@/components/common/NextImage';
import NPLButton from '@/components/npl/NPLButton';

function AnnouncementCard({ announcement, setHaveSeenAnnouncement }) {
  const { isGtEqMd } = useWindowWidthContext();
  const router = useRouter();
  const announcementImage = announcement?.image;

  const imgProps = {
    alt: 'nas.io hometab announcement image',
    mobileImgProps: {
      src: announcementImage,
      layout: 'responsive',
      width: 320,
      height: 215
    },
    tabletImgProps: {
      src: announcementImage,
      layout: 'fixed',
      width: 180,
      height: 120
    },
    desktopImgProps: {
      src: announcementImage,
      layout: 'fixed',
      width: 180,
      height: 120
    }
  };

  const handleCTAOnClick = (cta) => {
    const { link, openNewTab } = cta;
    if (!link) return;
    openNewTab ? openInNewTab(link) : router.push(link);
  };

  return (
    <div className="flex w-full flex-col gap-24 rounded-16 shadow-npl-styles-shadow-01 md:flex-row md:p-24">
      <div className="relative flex justify-between">
        <NextImage
          className="aspect-[3/2] flex-grow rounded-t-12 object-cover md:rounded-12"
          {...imgProps}
        />
      </div>
      <div className="flex w-full flex-col justify-between p-16 md:p-0">
        <div>
          <div className="text-label-lg font-semibold">
            {announcement?.title}
          </div>
          <div className="mt-8 line-clamp-2 max-w-[640px] text-body-sm text-npl-text-icon-on-light-surface-secondary/65">
            {announcement?.description}
          </div>
        </div>

        <div className="mt-24 flex md:justify-between">
          <div className="flex gap-8">
            {announcement?.cta && (
              <NPLButton
                hierarchy="neutral_primary"
                buttonText={announcement?.cta?.name}
                size={isGtEqMd ? 'sm' : 'md'}
                onClick={() => {
                  handleCTAOnClick(announcement?.cta);
                  trackGAEvent(ANNOUNCEMENT_CARD_EVENTS.PRIMARY_CTA_CLICK);
                }}
              />
            )}
            {announcement?.secondaryCta && (
              <NPLButton
                hierarchy="plain"
                buttonText={announcement?.secondaryCta?.name}
                size={isGtEqMd ? 'sm' : 'md'}
                onClick={() => {
                  handleCTAOnClick(announcement?.secondaryCta);
                  trackGAEvent(
                    ANNOUNCEMENT_CARD_EVENTS.SECONDARY_CTA_CLICK
                  );
                }}
              />
            )}
          </div>
          <div className="absolute right-24 top-32 md:static">
            <NPLButton
              hierarchy={isGtEqMd ? 'plain' : 'outline'}
              buttonText={t('got-it')}
              leadIcon="check"
              size="sm"
              onClick={() => {
                setHaveSeenAnnouncement(announcement?._id);
                trackGAEvent(
                  ANNOUNCEMENT_CARD_EVENTS.SEEN_ANNOUNCEMENT_CLICK
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnnouncementCard;
